// BodyMetricsEditModal — единен модал за телесни измервания (тегло, ръст,
// шия, талия, ханш). Използва се на dashboard (DIL card), DilHeroCard и
// в докторския профил, за да може админ/лекар да коригира собствените си
// измервания и веднага да преизчисли DIL / здравните индекси.
//
// След успешен запис компонентът:
//   1. PUT /api/user/profile с всички 5 полета
//   2. Извиква onSaved() ако е подаден (за локален refresh)
//   3. Dispatch на window event "nutramilo:body-metrics-updated" за да
//      уведоми други dashboard карти (DIL, HI summary) да презаредят.

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Ruler, X, Loader2 } from "lucide-react";

const FIELDS = [
  { key: "height",  label_bg: "Височина", label_en: "Height", unit: "cm", min: 100, max: 230, step: 0.5 },
  { key: "weight",  label_bg: "Тегло",    label_en: "Weight", unit: "kg", min: 30,  max: 250, step: 0.1 },
  { key: "neck",    label_bg: "Обиколка на шия",   label_en: "Neck",  unit: "cm", min: 20,  max: 60,  step: 0.5 },
  { key: "waist",   label_bg: "Обиколка на талия", label_en: "Waist", unit: "cm", min: 40,  max: 200, step: 0.5 },
  { key: "hip",     label_bg: "Обиколка на ханш",  label_en: "Hip",   unit: "cm", min: 50,  max: 200, step: 0.5 },
];

export default function BodyMetricsEditModal({
  open,
  onClose,
  API,
  user,
  isDark = false,
  bg = true,
  onSaved,
}) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  const syncFromUser = useCallback(() => {
    const p = user?.profile || {};
    const bd = p.body || {};
    const ms = p.measurements || {};
    setValues({
      height: bd.height ?? p.personal?.height_cm ?? "",
      weight: bd.weight ?? p.personal?.weight_kg ?? "",
      neck:   ms.neck ?? "",
      waist:  ms.waist ?? "",
      hip:    ms.hip ?? "",
    });
  }, [user]);

  useEffect(() => { if (open) syncFromUser(); }, [open, syncFromUser]);

  const updateField = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    // Basic validation for required fields
    const h = parseFloat(values.height);
    const w = parseFloat(values.weight);
    if (!h || h < 100 || h > 230) {
      toast.error(bg ? "Височина: 100–230 cm" : "Height: 100–230 cm");
      return;
    }
    if (!w || w < 30 || w > 250) {
      toast.error(bg ? "Тегло: 30–250 kg" : "Weight: 30–250 kg");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        height: h,
        weight: w,
        neck:  values.neck  ? parseFloat(values.neck)  : undefined,
        waist: values.waist ? parseFloat(values.waist) : undefined,
        hip:   values.hip   ? parseFloat(values.hip)   : undefined,
        // Legacy field aliases (used by DIL / NIS engines)
        neckCircumference:  values.neck  ? parseFloat(values.neck)  : undefined,
        waistCircumference: values.waist ? parseFloat(values.waist) : undefined,
        hipCircumference:   values.hip   ? parseFloat(values.hip)   : undefined,
      };
      await axios.put(`${API}/user/profile`, payload);
      setSavedTick(true);
      toast.success(bg ? "Телесните измервания са обновени · DIL се преизчислява" : "Body metrics updated · DIL recalculating");
      // Notify all dashboard cards to refetch
      window.dispatchEvent(new CustomEvent("nutramilo:body-metrics-updated", { detail: payload }));
      if (onSaved) await onSaved();
      setTimeout(() => { setSavedTick(false); onClose(); }, 900);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка при запис." : "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const inputCls = `flex-1 px-3 py-2 rounded-lg text-base font-semibold tabular-nums border ${
    isDark ? "bg-white/5 border-white/10 text-white" : "bg-gray-50 border-gray-200 text-[#1A1A2E]"
  }`;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="body-metrics-modal"
    >
      <div
        className={`w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl p-6 max-h-[90vh] overflow-y-auto shadow-2xl ${
          isDark ? "bg-[hsl(222,47%,12%)] text-white" : "bg-white text-[#1A1A2E]"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Ruler className="w-5 h-5 text-emerald-500" />
            <h3 className="text-lg font-bold">
              {bg ? "Телесни измервания" : "Body metrics"}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`p-1 rounded-full ${isDark ? "hover:bg-white/10" : "hover:bg-gray-100"}`}
            aria-label={bg ? "Затвори" : "Close"}
            data-testid="body-metrics-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className={`text-xs leading-relaxed mb-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          {bg
            ? "Всички клинични показатели (DIL, NIS, BHI) зависят от тези стойности. Запазете промените и веднага ще видите преизчислените цели."
            : "All clinical indices (DIL, NIS, BHI) depend on these values. Save to see recalculated targets instantly."}
        </p>

        <div className="space-y-3">
          {FIELDS.map((f) => (
            <div key={f.key} className="flex items-center gap-3">
              <label className={`text-xs font-semibold w-32 sm:w-40 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {bg ? f.label_bg : f.label_en}
              </label>
              <input
                type="number"
                step={f.step}
                min={f.min}
                max={f.max}
                value={values[f.key] ?? ""}
                onChange={(e) => updateField(f.key, e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") save(); }}
                data-testid={`body-metrics-input-${f.key}`}
                className={inputCls}
                placeholder={`${f.min}–${f.max}`}
              />
              <span className={`text-xs font-semibold w-8 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {f.unit}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-6 flex gap-2">
          <button
            type="button"
            onClick={save}
            disabled={saving || savedTick}
            data-testid="body-metrics-save"
            className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-bold transition-colors ${
              savedTick
                ? (isDark ? "bg-emerald-500/30 text-emerald-100" : "bg-emerald-200 text-emerald-900")
                : (isDark ? "bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30" : "bg-emerald-600 text-white hover:bg-emerald-700")
            }`}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {savedTick ? `✓ ${bg ? "Записано · преизчислява се" : "Saved · recalculating"}` : (bg ? "Запази и преизчисли" : "Save & recalculate")}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2.5 rounded-full text-sm font-semibold ${
              isDark ? "text-gray-300 hover:text-white" : "text-gray-700 hover:text-[#1A1A2E]"
            }`}
          >
            {bg ? "Отказ" : "Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}
